import React, { useState, useEffect } from 'react'
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  useTheme
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { toast } from 'react-hot-toast'

import {
  useCreatePipelineMutation,
  useGetPipelineStagesByWorkspaceQuery
} from 'src/services/dealpipeline-crm-service'

const PipelineCreationFlow = ({ open, onClose, workspaceId, userId }) => {
  const [step, setStep] = useState(0)
  const [name, setName] = useState('')
  const [stages, setStages] = useState([{ type: 'new', title: '', valuePerDeal: '' }])
  const [isCreating, setIsCreating] = useState(false)
  const theme = useTheme()

  const [createPipeline] = useCreatePipelineMutation()
  const {
    data: existingStages = [],
    refetch
  } = useGetPipelineStagesByWorkspaceQuery(workspaceId)
  // Ensure fresh data when modal opens
  useEffect(() => {
    if (open) {
      // Don't include refetch in deps to avoid unstable reference issues
      refetch()
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setStep(0)
      setName('')
      setStages([{ type: 'new', title: '', valuePerDeal: '' }])
      setIsCreating(false)
    }
  }, [open])

  const handleStageChange = (index, field, value) => {
    const updated = [...stages]
    if (field === 'valuePerDeal') {
      const num = parseFloat(value)
      updated[index][field] = !isNaN(num) && num >= 0 ? num : ''
    } else {
      updated[index][field] = value
    }
    setStages(updated)
  }

  const handleStageTypeChange = (index, value) => {
    const updated = [...stages]

    if (value === 'new') {
      updated[index] = { type: 'new', title: '', valuePerDeal: '' }
    } else {
      const selected = existingStages.find((s) => s._id === value)

      const isAlreadyAdded = stages.some(
        (stage, i) =>
          i !== index &&
          stage.type === 'existing' &&
          stage.stageId === selected._id
      )

      if (isAlreadyAdded) {
        toast.error('This stage has already been added.')
        return
      }

      updated[index] = {
        type: 'existing',
        stageId: selected._id,
        title: selected.title,
        valuePerDeal: selected.valuePerDeal
      }
    }

    setStages(updated)
  }

  const handleAddStage = () => {
    setStages([...stages, { type: 'new', title: '', valuePerDeal: '' }])
  }

  const handleRemoveStage = (index) => {
    if (stages.length > 1) {
      const updated = stages.filter((_, i) => i !== index)
      setStages(updated)
    }
  }

  const isFormValid = () =>
    name.trim() &&
    stages.every(
      (s) =>
        s.title.trim() &&
        s.valuePerDeal !== '' &&
        !isNaN(s.valuePerDeal) &&
        Number(s.valuePerDeal) >= 0
    )

  const handleSubmit = async () => {
    if (!isFormValid()) return
    setIsCreating(true)

    const mappedStages = stages.map((s, i) => ({
      title: s.title,
      valuePerDeal: s.valuePerDeal,
      order: i,
      ...(s.type === 'existing' ? { labelId: s.stageId } : {})
    }))

    const payload = {
      title: name,
      type: 'custom',
      workspaceId,
      description: 'Created via UI flow',
      createdBy: userId,
      stages: mappedStages
    }

    try {
      await createPipeline(payload).unwrap()
      toast.success('Pipeline created successfully')
      refetch()
      onClose()
    } catch (err) {
      toast.error('Failed to create pipeline')
    } finally {
      setIsCreating(false)
    }
  }

  const canClose = step === 0 || (stages.length > 0 && isFormValid())
  const canAddStage =
    stages[stages.length - 1].title.trim() &&
    stages[stages.length - 1].valuePerDeal !== '' &&
    !isNaN(stages[stages.length - 1].valuePerDeal) &&
    Number(stages[stages.length - 1].valuePerDeal) >= 0

  return (
    <Modal
      open={open}
      onClose={() => {
        if (canClose) onClose()
      }}
      disableEscapeKeyDown={!canClose}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80vw',
          maxWidth: 700,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: '12px',
          p: 4
        }}
      >
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Pipeline Name</StepLabel>
          </Step>
          <Step>
            <StepLabel>Stages / Columns</StepLabel>
          </Step>
        </Stepper>

        {step === 0 && (
          <>
            <Typography variant='h6' fontWeight='700' mb={2}>
              Name your pipeline
            </Typography>
            <TextField
              fullWidth
              label='Pipeline name'
              variant='outlined'
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 4 }}
            />
            <Box display='flex' justifyContent='flex-end' gap={2}>
              <Button onClick={onClose} disabled={isCreating}>
                Cancel
              </Button>
              <Button
                variant='contained'
                sx={{
                  color: 'white',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: 10
                  }
                }}
                disabled={!name.trim()}
                onClick={() => setStep(1)}
              >
                Next
              </Button>
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant='h6' fontWeight='700' mb={2}>
              Define Stages / Columns
            </Typography>

            {stages.map((stage, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Select
                  size='small'
                  value={stage.type === 'new' ? 'new' : stage.stageId}
                  onChange={(e) => handleStageTypeChange(index, e.target.value)}
                  displayEmpty
                  sx={{
                    width: 300,
                    cursor: 'pointer',
                    '& .MuiSelect-select': {
                      py: 1.2,
                      fontSize: '0.875rem'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E0E0E0'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3F50F8'
                    }
                  }}
                >
                  <MenuItem value='new'>+ New Stage</MenuItem>
                  {existingStages
                    .filter((s) => {
                      const isAlreadySelected = stages.some(
                        (st, i) => i !== index && st.type === 'existing' && st.stageId === s._id
                      )
                      return !isAlreadySelected
                    })
                    .map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.title}
                      </MenuItem>
                    ))}
                </Select>

                <TextField
                  label='Stage name'
                  variant='outlined'
                  fullWidth
                  value={stage.title}
                  onChange={(e) => handleStageChange(index, 'title', e.target.value)}
                  disabled={stage.type === 'existing'}
                />

                <TextField
                  label='Value ($)'
                  variant='outlined'
                  type='number'
                  value={stage.valuePerDeal}
                  onChange={(e) => handleStageChange(index, 'valuePerDeal', e.target.value)}
                  sx={{ width: 150 }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />

                <IconButton onClick={() => handleRemoveStage(index)} disabled={stages.length === 1}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              onClick={handleAddStage}
              sx={{ mb: 2 }}
              disabled={!canAddStage}
              variant='outlined'
            >
              + Add Stage
            </Button>

            <Box display='flex' justifyContent='flex-end' gap={2}>
              <Button
                onClick={() => !isCreating && setStep(0)}
                disabled={isCreating}
                variant='outlined'
              >
                Back
              </Button>
              <Button
                variant='contained'
                disabled={!isFormValid() || isCreating}
                onClick={handleSubmit}
              >
                {isCreating
                  ? (
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    )
                  : (
                      'Create Pipeline'
                    )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default PipelineCreationFlow
